import React, { FC, useMemo } from 'react';

import { splitArrayChunks } from '@sv-support-sdk/utils/src/helpers/splitArrayChunks';
import { topicsSorting } from '@sv-support-sdk/utils/src/helpers/topicsSorting';
import { MIN_PER_CHUNK, CHUNKS_COUNT } from '@sv-support-sdk/utils/src/constants/articles';
import { IHelpCenterCategory, IHelpCenterTopic } from '@sv-support-sdk/shared-types/src/common/helpCenter'
import List from '@sv-support-sdk/ui-components/src/components/Lists/List';

import Link from '../../Controls/Link';

import { useAppSelector } from '../../../store/hooks';
import {
  categoriesByGrandSelector,
  topicsByCategorySlugSelector,
} from '../../../store/selectors/helpCenter';
import { routes } from '../../../routing';

type TMappableTopicsAndCategories = ((IHelpCenterTopic | IHelpCenterCategory) & {
  isCategory?: boolean;
  key?: string;
})[];

interface IProps {
  category: IHelpCenterCategory;
  noTopicsAsCategories?: boolean;
  className?: string;
}

const CategoryBlockList: FC<IProps> = ({ category, noTopicsAsCategories, className }) => {
  const topics = useAppSelector((state) => topicsByCategorySlugSelector(state, category.slug));
  const subCategories = useAppSelector((state) => categoriesByGrandSelector(state, category.id));

  const sortLinkItems = (list: TMappableTopicsAndCategories) => [...list].sort(topicsSorting);

  const combineTopicsWithSubcategories = () => {
    const subcategoriesWithFlags = noTopicsAsCategories
      ? []
      : subCategories.map((cat) => ({ ...cat, isCategory: true }));

    const mappableTopicsAndCategories = sortLinkItems([...subcategoriesWithFlags, ...topics]).map(
      (item) => ({
        ...item,
        key: item.isCategory ? `${item.id}-category` : `${item.id}-topic`,
      }),
    );

    const itemsPerChunk = Math.ceil(mappableTopicsAndCategories.length / CHUNKS_COUNT);
    const perChunk = Math.max(itemsPerChunk, MIN_PER_CHUNK);

    return splitArrayChunks(mappableTopicsAndCategories, perChunk);
  };

  const chunks: TMappableTopicsAndCategories[] = useMemo(combineTopicsWithSubcategories, [
    topics,
    subCategories,
    noTopicsAsCategories,
  ]);

  if (chunks.length === 0) {
    return null;
  }

  return (
    <List isResponsive type="grid" columns="3" gap="24" className={className}>
      {chunks &&
        chunks.map(
          (chunk, index) =>
            chunk && (
              <List key={index} gap="16">
                {chunk.map((item) => (
                  <Link
                    key={item.key}
                    to={`${routes.articles}/${item.isCategory ? '' : `${category.slug}/`}${item.slug}`}
                  >
                    {item.name}
                  </Link>
                ))}
              </List>
            ),
        )}
    </List>
  );
};

export default CategoryBlockList;
